import { Season, type CompanyLocation } from "@mysnowscape/snow-lib";
import {
  EMPTY_ROUTE_ENDPOINT,
  RouteEndpointLocation,
  RouteGenerationInputs,
} from "../../../../../types/routeBuilder";
import {
  DEFAULT_MAX_ROUTE_DURATION_HOURS,
  maxRouteDurationMinutesFromHours,
} from "./helpers";
import { DEFAULT_SERVICE_TIME_MINUTES } from "./generationProperties";

function defaultDepot(locations: CompanyLocation[]): RouteEndpointLocation {
  return locations[0] ?? EMPTY_ROUTE_ENDPOINT;
}

export function defaultTemporaryExpiresAt(plannedDispatchTime: string): string {
  return new Date(
    new Date(plannedDispatchTime).getTime() + 2 * 24 * 60 * 60 * 1000,
  ).toISOString();
}

/** Fill missing start/end depot from company locations when still empty. */
export function resolveGenerationInputs(
  inputs: RouteGenerationInputs,
  locations: CompanyLocation[],
): RouteGenerationInputs {
  const fallback = defaultDepot(locations);
  const start =
    inputs.startLocation?.location != null ? inputs.startLocation : fallback;
  const end =
    inputs.endLocation?.location != null ? inputs.endLocation : fallback;
  // Older drafts have no linking flag. Coordinates alone must not merge
  // endpoints with different saved IDs or unsaved metadata.
  const sameStartAndEndLocation =
    inputs.sameStartAndEndLocation ??
    (start._id != null || end._id != null
      ? start._id?.toString() === end._id?.toString()
      : start.location?.lat === end.location?.lat &&
        start.location?.lng === end.location?.lng &&
        (start.name ?? "") === (end.name ?? "") &&
        (start.address ?? "") === (end.address ?? ""));

  return {
    ...inputs,
    incorporateTime: inputs.incorporateTime ?? true,
    defaultServiceTimeMinutes:
      inputs.defaultServiceTimeMinutes ?? DEFAULT_SERVICE_TIME_MINUTES,
    excludedPropertyIds: inputs.excludedPropertyIds ?? [],
    startLocation: start,
    endLocation: sameStartAndEndLocation ? start : end,
    sameStartAndEndLocation,
  };
}

export function defaultInputs(
  locations: CompanyLocation[],
): RouteGenerationInputs {
  const depot = defaultDepot(locations);
  return {
    routeType: "recurring",
    season: Season.snow,
    propertyFilters: {
      serviceTypes: [],
      recencyOfService: null,
    },
    routeCount: 4,
    incorporateTime: true,
    defaultServiceTimeMinutes: DEFAULT_SERVICE_TIME_MINUTES,
    excludedPropertyIds: [],
    maxRouteDurationMinutes: maxRouteDurationMinutesFromHours(
      DEFAULT_MAX_ROUTE_DURATION_HOURS,
    ),
    plannedDispatchTime: new Date().toISOString(),
    startLocation: depot,
    endLocation: depot,
    sameStartAndEndLocation: true,
  };
}
